import {
  createFeatureSelector,
  createSelector,
  MemoizedSelector,
} from '@ngrx/store';
import * as fromUsersStore from '../reducers/employee.reducer';
import * as fromUserGroupsSelectors from './employee-groups.selectors';
import { getEmployeeGroups } from './employee-groups.selectors';
import { Employee } from '@fc-core';
import { getAllInvites, getInvitesLoading } from './invite.selectors';
import { sortByPropertyWithOrder } from '@fc-shared/utils/sort-by-property';
import { selectRouteParams } from '@fc-store/selectors/router.selectors';
import * as fromEmployees from '@fc-store/reducers/employee.reducer';
import {
  buildEmployeeGroupListItems,
  countEmployeesByGroupId,
  filterEmployeesByGroup,
  filterEmployeesByState,
  searchEmployees,
} from './employee-list-filters';

export const getEmployeeState = createFeatureSelector<fromEmployees.State>(
  fromEmployees.employeesFeatureKey,
);
export const selectRawEmployees = createSelector(
  getEmployeeState,
  fromUsersStore.selectAll,
);

export const getEmployeeEntities = createSelector(
  getEmployeeState,
  fromUsersStore.selectEntities,
);

const employeeByIdSelectors = new Map<
  string,
  MemoizedSelector<object, Employee | undefined>
>();

export const getEmployeeById = (id: string | null | undefined) => {
  const employeeId = id ?? '';
  let selector = employeeByIdSelectors.get(employeeId);
  if (!selector) {
    selector = createSelector(
      getEmployeeEntities,
      (employees) => employees[employeeId],
    );
    employeeByIdSelectors.set(employeeId, selector);
  }
  return selector;
};

export const getEmployeeSortBy = createSelector(
  getEmployeeState,
  fromUsersStore.getSortBy,
);
export const getEmployeeSortByList = createSelector(
  getEmployeeState,
  fromUsersStore.getSortByList,
);

export const getLoadings = createSelector(
  getEmployeeState,
  fromUsersStore.getLoading,
);

export const getEmployeeLoading = createSelector(
  getInvitesLoading,
  getLoadings,
  (inviteLoadings, userLoadings) => userLoadings || inviteLoadings,
);
export const getUserFilters = createSelector(
  getEmployeeState,
  fromUsersStore.getUserFilters,
);
export const getSelectedEmployeeFilters = createSelector(
  getEmployeeState,
  fromUsersStore.getSelectedEmployeeFilters,
);
export const getUsersSearchText = createSelector(
  getEmployeeState,
  fromUsersStore.getSearchText,
);
export const getUsersCount = createSelector(
  getEmployeeState,
  fromUsersStore.getUsersCount,
);

export const getCurrentEmployee = createSelector(
  getEmployeeState,
  fromUsersStore.getCurrentEmployee,
);

export const getUsersLoadProgress = createSelector(
  getUsersCount,
  selectRawEmployees,
  (userCount: number, allUsers: Employee[]) =>
    (allUsers?.length / userCount) * 100,
);

export const getSelectedUserGroupFilter = createSelector(
  getEmployeeState,
  fromUsersStore.getSelectedUserGroupFilter,
);
export const getOperators = createSelector(
  selectRawEmployees,
  (users: Employee[]) => users.filter((user) => user.operator),
);

const formattedEmployeeCache = new WeakMap<Employee, Employee>();

export const getFormattedEmployees = createSelector(
  selectRawEmployees,
  getAllInvites,
  (employees, invites): Employee[] => {
    const invitedEmployeeIds = new Set(
      invites.map((invite) => invite.employee),
    );
    return employees.map((employee) => {
      const hasInvite = invitedEmployeeIds.has(employee.id ?? '');
      const cachedEmployee = formattedEmployeeCache.get(employee);
      if (cachedEmployee?.hasInvite === hasInvite) return cachedEmployee;
      const formattedEmployee = { ...employee, hasInvite };
      formattedEmployeeCache.set(employee, formattedEmployee);
      return formattedEmployee;
    });
  },
);

export const getActiveEmployees = createSelector(
  getFormattedEmployees,
  (employees) => employees.filter((employee) => !employee.archived),
);

export const getEmployeesWithoutGroupFilter = createSelector(
  getFormattedEmployees,
  getEmployeeSortBy,
  getUsersSearchText,
  getSelectedEmployeeFilters,
  (employees, sortBy, searchText, selectedFilters) => {
    const sortedEmployees = sortByPropertyWithOrder<Employee>(
      employees,
      sortBy.value,
      sortBy.order ?? false,
    );
    return filterEmployeesByState(
      searchEmployees(sortedEmployees, searchText),
      selectedFilters,
    );
  },
);

export const getEmployees = createSelector(
  getEmployeesWithoutGroupFilter,
  getSelectedUserGroupFilter,
  filterEmployeesByGroup,
);

export const getEmployeeGroupsWithCount = createSelector(
  getEmployeesWithoutGroupFilter,
  getEmployeeGroups,
  (employees, groups) => {
    const employeeCountByGroupId = countEmployeesByGroupId(employees);
    const allEmployeesGroup = {
      id: -1,
      name: `All employees (${employees.length})`,
      color: '#B0BEC5',
    };
    const groupsWithCount = groups.map((group) => ({
      ...group,
      name: `${group.name} (${employeeCountByGroupId[group.id] ?? 0})`,
    }));
    return [allEmployeesGroup, ...groupsWithCount];
  },
);

export const getActiveEmployeeCountByGroupId = createSelector(
  selectRawEmployees,
  (employees) =>
    countEmployeesByGroupId(employees.filter((employee) => !employee.archived)),
);

export const getEmployeeGroupListItems = createSelector(
  fromUserGroupsSelectors.getAllUserGroups,
  fromUserGroupsSelectors.getUserGroupSearchText,
  fromUserGroupsSelectors.getEmployeeGroupSortBy,
  getActiveEmployeeCountByGroupId,
  buildEmployeeGroupListItems,
);

export const getSelectedEmployeeGroupWithCounter = createSelector(
  getSelectedUserGroupFilter,
  getEmployeesWithoutGroupFilter,
  (selectedUserGroup, employees) => {
    return !selectedUserGroup || selectedUserGroup.id === -1
      ? {
          name: `All employees (${employees.length})`,
          id: -1,
          color: '#B0BEC5',
        }
      : selectedUserGroup;
  },
);

export const getEmployeesForCurrentUserGroup = createSelector(
  getFormattedEmployees,
  fromUserGroupsSelectors.getCurrentUserGroup,
  (employees, userGroup): Employee[] => {
    if (!userGroup) return [];
    return employees.filter((employee) =>
      employee.groups?.includes(userGroup.id),
    );
  },
);

export const getInviteByEmployeeId = (employeeId: string) =>
  createSelector(getAllInvites, (invites) =>
    invites.find((invite) => invite.employee === employeeId),
  );

export const getSelectedEmployee = createSelector(
  selectRouteParams,
  getFormattedEmployees,
  ({ id }, employees) => {
    if (!id) return null;
    return employees.find((user) => user?.id === id);
  },
);
